import { useState, useEffect } from 'react';
import { useAuthContext } from '@/components/auth/ClerkAuthProvider';
import {
  useTenantCompat as useTenant,
  useUpdateWidgetConfigCompat as useUpdateWidgetConfig,
  useAddCustomColorCompat as useAddCustomColor,
  useUpdateCustomColorCompat as useUpdateCustomColor,
  useDeleteCustomColorCompat as useDeleteCustomColor,
  getAvailableColors,
} from '@/hooks/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { AddCustomColorDialog, EditCustomColorDialog, DeleteColorConfirmDialog, SocialProofSettings, RoomConfigSettings } from '@/components/settings';
import { Pagination } from '@/components/widget/ColorPicker/Pagination';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Code,
  Loader2,
  Save,
  Link,
  Phone,
  Copy,
  Check,
  ExternalLink,
  Paintbrush,
  Plus,
  Pencil,
  Trash2,
  FileCode,
  BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import type { WidgetConfig, SherwinWilliamsColor } from '@/types/firestore';
import { getDefaultWidgetConfig } from '@/types/firestore';
import { SHERWIN_WILLIAMS_COLORS, DEFAULT_FEATURED_COLORS } from '@/data/sherwin-williams-colors';
import type { CustomColorFormData } from '@/lib/validations';

const COLORS_PER_PAGE = 24;

export default function WidgetSettings() {
  const { tenantId } = useAuthContext();
  const { data: tenant, isLoading } = useTenant(tenantId ?? undefined);
  const updateWidget = useUpdateWidgetConfig();

  // Widget config state
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig>(getDefaultWidgetConfig());
  const [embedCopied, setEmbedCopied] = useState(false);
  const [colorPage, setColorPage] = useState(1);

  // Custom color dialog state
  const [addColorDialogOpen, setAddColorDialogOpen] = useState(false);
  const [editColorDialogOpen, setEditColorDialogOpen] = useState(false);
  const [deleteColorDialogOpen, setDeleteColorDialogOpen] = useState(false);
  const [selectedCustomColor, setSelectedCustomColor] = useState<SherwinWilliamsColor | null>(null);
  const [customColorError, setCustomColorError] = useState<string | undefined>();

  // Custom color mutation hooks
  const addCustomColor = useAddCustomColor();
  const updateCustomColor = useUpdateCustomColor();
  const deleteCustomColor = useDeleteCustomColor();

  // Load widget config from tenant
  useEffect(() => {
    if (tenant) {
      setWidgetConfig(tenant.widget_config || getDefaultWidgetConfig());
    }
  }, [tenant]);

  // Custom color handlers
  const handleAddCustomColor = async (color: CustomColorFormData) => {
    if (!tenantId) return;
    setCustomColorError(undefined);
    try {
      await addCustomColor.mutateAsync({ tenantId, color });
      setAddColorDialogOpen(false);
      toast.success(`Custom color "${color.name}" added!`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add color';
      setCustomColorError(message);
      throw error;
    }
  };

  const handleEditCustomColor = async (code: string, color: CustomColorFormData) => {
    if (!tenantId) return;
    setCustomColorError(undefined);
    try {
      await updateCustomColor.mutateAsync({ tenantId, code, color });
      setEditColorDialogOpen(false);
      setSelectedCustomColor(null);
      toast.success(`Custom color "${color.name}" updated!`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update color';
      setCustomColorError(message);
      throw error;
    }
  };

  const handleDeleteCustomColor = async () => {
    if (!tenantId || !selectedCustomColor) return;
    try {
      await deleteCustomColor.mutateAsync({ tenantId, code: selectedCustomColor.code });
      setDeleteColorDialogOpen(false);
      setSelectedCustomColor(null);
      toast.success(`Custom color "${selectedCustomColor.name}" deleted!`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete color';
      toast.error(message);
    }
  };

  const openEditDialog = (color: SherwinWilliamsColor) => {
    setSelectedCustomColor(color);
    setCustomColorError(undefined);
    setEditColorDialogOpen(true);
  };

  const openDeleteDialog = (color: SherwinWilliamsColor) => {
    setSelectedCustomColor(color);
    setDeleteColorDialogOpen(true);
  };

  // Get available colors
  const availableColors = getAvailableColors(SHERWIN_WILLIAMS_COLORS, tenant?.custom_colors);
  const customColors = tenant?.custom_colors || [];
  const customColorCodes = new Set(customColors.map((c) => c.code));

  // Script tag embed (recommended)
  const getScriptEmbedCode = () => {
    if (!tenant?.slug) return '';
    return `<script src="${window.location.origin}/widget/${tenant.slug}.js"></script>`;
  };

  // Iframe embed (alternative)
  const getIframeEmbedCode = () => {
    if (!tenant?.slug) return '';
    const widgetUrl = `${window.location.origin}/widget/${tenant.slug}`;
    return `<iframe
  src="${widgetUrl}"
  width="100%"
  height="800"
  frameborder="0"
  allow="camera"
  style="max-width: 480px; border-radius: 12px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);"
></iframe>`;
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setEmbedCopied(true);
      toast.success(`${label} copied!`);
      setTimeout(() => setEmbedCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleSaveWidget = async () => {
    if (!tenantId) return;

    // Validate URLs
    const urlPattern = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+[/#?]?.*$/;
    if (widgetConfig.cta_book_call_url && !urlPattern.test(widgetConfig.cta_book_call_url)) {
      toast.error('Please enter a valid Book a Call URL');
      return;
    }
    if (widgetConfig.cta_get_quote_url && !urlPattern.test(widgetConfig.cta_get_quote_url)) {
      toast.error('Please enter a valid Get Quote URL');
      return;
    }

    // Validate phone
    if (widgetConfig.business_phone) {
      const phoneDigits = widgetConfig.business_phone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        toast.error('Please enter a valid 10-digit phone number');
        return;
      }
    }

    // Validate watermark
    if (widgetConfig.watermark_text && widgetConfig.watermark_text.length > 50) {
      toast.error('Watermark text must be less than 50 characters');
      return;
    }

    try {
      await updateWidget.mutateAsync({
        tenantId,
        widget_config: widgetConfig,
      });
      toast.success('Widget settings saved!');
    } catch {
      toast.error('Failed to save widget settings');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Widget Enable/Behavior Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            Widget Configuration
          </CardTitle>
          <CardDescription>Configure your widget behavior and appearance</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="widget-enabled" className="text-base font-medium">
                Widget Enabled
              </Label>
              <p className="text-sm text-muted-foreground">
                Allow visitors to use your renovation widget
              </p>
            </div>
            <Switch
              id="widget-enabled"
              checked={widgetConfig.enabled}
              onCheckedChange={(checked) =>
                setWidgetConfig((prev) => ({ ...prev, enabled: checked }))
              }
            />
          </div>

          <Separator />

          {/* Behavior Toggles */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-medium">Widget Behavior</Label>
              <p className="text-sm text-muted-foreground">
                Configure what information to show and collect
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="require-address" className="font-medium">
                  Require Project Address
                </Label>
                <p className="text-sm text-muted-foreground">
                  Collect street address for job location
                </p>
              </div>
              <Switch
                id="require-address"
                checked={widgetConfig.require_address !== false}
                onCheckedChange={(checked) =>
                  setWidgetConfig((prev) => ({ ...prev, require_address: checked }))
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="require-timeline" className="font-medium">
                  Require Project Timeline
                </Label>
                <p className="text-sm text-muted-foreground">
                  Ask when the customer wants to start the project
                </p>
              </div>
              <Switch
                id="require-timeline"
                checked={widgetConfig.require_timeline !== false}
                onCheckedChange={(checked) =>
                  setWidgetConfig((prev) => ({ ...prev, require_timeline: checked }))
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Paint Color Selection Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Paintbrush className="h-5 w-5" />
            Paint Color Selection
          </CardTitle>
          <CardDescription>
            Choose which Sherwin-Williams colors to feature in your widget
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Allow Color Search */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="allow-color-search" className="font-medium">
                Allow Color Search
              </Label>
              <p className="text-sm text-muted-foreground">
                Let customers search all SW colors (otherwise only featured colors shown)
              </p>
            </div>
            <Switch
              id="allow-color-search"
              checked={widgetConfig.allow_color_search !== false}
              onCheckedChange={(checked) =>
                setWidgetConfig((prev) => ({ ...prev, allow_color_search: checked }))
              }
            />
          </div>

          <Separator />

          {/* Custom Colors */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Custom Colors</Label>
                <p className="text-xs text-muted-foreground">
                  Add your own Sherwin-Williams colors ({customColors.length}/50)
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setCustomColorError(undefined);
                  setAddColorDialogOpen(true);
                }}
                disabled={customColors.length >= 50}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Color
              </Button>
            </div>

            {customColors.length > 0 ? (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                {customColors.map((color) => (
                  <div
                    key={color.code}
                    className="group relative flex flex-col items-center gap-1 p-2 rounded-lg border-2 border-muted bg-muted/20"
                  >
                    <div
                      className="w-8 h-8 rounded shadow-sm"
                      style={{ backgroundColor: color.hex }}
                    />
                    <span className="text-[10px] text-center truncate w-full">{color.name}</span>
                    <Badge variant="secondary" className="text-[8px] px-1 py-0">
                      Custom
                    </Badge>
                    <div className="absolute inset-0 bg-background/80 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => openEditDialog(color)}
                        title="Edit color"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => openDeleteDialog(color)}
                        title="Delete color"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 border-2 border-dashed border-muted rounded-lg">
                <p className="text-sm text-muted-foreground">No custom colors added yet</p>
                <Button
                  size="sm"
                  variant="link"
                  onClick={() => {
                    setCustomColorError(undefined);
                    setAddColorDialogOpen(true);
                  }}
                  className="mt-1"
                >
                  Add your first custom color
                </Button>
              </div>
            )}
          </div>

          <Separator />

          {/* Featured Colors */}
          <div className="space-y-3">
            <Label className="font-medium">Featured Colors (select up to 8)</Label>
            <p className="text-xs text-muted-foreground">
              These colors will be prominently displayed for customers to choose from.
              {customColors.length > 0 && ' Custom colors are marked with a badge.'}
            </p>
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
              {availableColors
                .slice((colorPage - 1) * COLORS_PER_PAGE, colorPage * COLORS_PER_PAGE)
                .map((color) => {
                  const featuredColors =
                    widgetConfig.featured_paint_colors || DEFAULT_FEATURED_COLORS;
                  const isSelected = featuredColors.includes(color.code);
                  const canSelect = isSelected || featuredColors.length < 8;
                  const isCustom = customColorCodes.has(color.code);

                  return (
                    <button
                      key={color.code}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          setWidgetConfig((prev) => ({
                            ...prev,
                            featured_paint_colors: (
                              prev.featured_paint_colors || DEFAULT_FEATURED_COLORS
                            ).filter((c) => c !== color.code),
                          }));
                        } else if (canSelect) {
                          setWidgetConfig((prev) => ({
                            ...prev,
                            featured_paint_colors: [
                              ...(prev.featured_paint_colors || DEFAULT_FEATURED_COLORS),
                              color.code,
                            ],
                          }));
                        }
                      }}
                      disabled={!canSelect && !isSelected}
                      className={`group relative flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                          : canSelect
                          ? 'border-muted hover:border-muted-foreground/30'
                          : 'border-muted opacity-50 cursor-not-allowed'
                      }`}
                      title={`${color.name} (${color.code})${isCustom ? ' - Custom' : ''}`}
                    >
                      <div
                        className="w-8 h-8 rounded shadow-sm"
                        style={{ backgroundColor: color.hex }}
                      />
                      <span className="text-[10px] text-center truncate w-full">{color.name}</span>
                      {isCustom && (
                        <Badge variant="secondary" className="text-[8px] px-1 py-0">
                          Custom
                        </Badge>
                      )}
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  );
                })}
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {(widgetConfig.featured_paint_colors || DEFAULT_FEATURED_COLORS).length}/8 colors
                selected
              </p>
              <Pagination
                currentPage={colorPage}
                totalPages={Math.ceil(availableColors.length / COLORS_PER_PAGE)}
                onPageChange={setColorPage}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Social Proof Settings */}
      <SocialProofSettings
        widgetConfig={widgetConfig}
        onConfigChange={setWidgetConfig}
      />

      {/* Room Configuration Settings */}
      <RoomConfigSettings
        roomConfig={widgetConfig.room_config}
        onChange={(config) => setWidgetConfig((prev) => ({ ...prev, room_config: config }))}
      />

      {/* Embed Code & CTAs Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            Integration & CTAs
          </CardTitle>
          <CardDescription>
            Embed code and call-to-action settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Embed Code */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Embed Code</Label>
            <p className="text-sm text-muted-foreground">
              Copy this code and paste it into your website
            </p>

            <Tabs defaultValue="script" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="script" className="gap-2">
                  <FileCode className="h-4 w-4" />
                  Script Tag
                  <Badge variant="secondary" className="ml-1 text-[10px]">Recommended</Badge>
                </TabsTrigger>
                <TabsTrigger value="iframe" className="gap-2">
                  <Code className="h-4 w-4" />
                  Iframe
                </TabsTrigger>
              </TabsList>

              <TabsContent value="script" className="space-y-3 mt-4">
                <p className="text-sm text-muted-foreground">
                  One-line embed that auto-initializes. Works with WordPress, Squarespace, Wix, and any HTML page.
                </p>
                <div className="relative">
                  <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all font-mono">
                    {getScriptEmbedCode()}
                  </pre>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(getScriptEmbedCode(), 'Script embed code')}
                  >
                    {embedCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <BookOpen className="h-3 w-3" />
                  <span>See installation guides below for platform-specific instructions</span>
                </div>
              </TabsContent>

              <TabsContent value="iframe" className="space-y-3 mt-4">
                <p className="text-sm text-muted-foreground">
                  Standard iframe embed with full control over dimensions.
                </p>
                <div className="relative">
                  <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all font-mono">
                    {getIframeEmbedCode()}
                  </pre>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(getIframeEmbedCode(), 'Iframe embed code')}
                  >
                    {embedCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`/widget/${tenant?.slug}`, '_blank')}
                disabled={!tenant?.slug}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Preview Widget
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('/docs/widget-installation', '_blank')}
              >
                <BookOpen className="mr-2 h-4 w-4" />
                Installation Guides
              </Button>
            </div>
          </div>

          <Separator />

          {/* CTA Links */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-medium">Call-to-Action Links</Label>
              <p className="text-sm text-muted-foreground">
                Where to send customers after they see their renovation preview
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cta-book-call">Book a Call URL</Label>
              <div className="flex items-center gap-2">
                <Link className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <Input
                  id="cta-book-call"
                  type="url"
                  value={widgetConfig.cta_book_call_url || ''}
                  onChange={(e) =>
                    setWidgetConfig((prev) => ({
                      ...prev,
                      cta_book_call_url: e.target.value || undefined,
                    }))
                  }
                  placeholder="https://calendly.com/your-link"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Link to your booking page (e.g., Calendly, Cal.com)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cta-quote">Get Quote URL</Label>
              <div className="flex items-center gap-2">
                <Link className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <Input
                  id="cta-quote"
                  type="url"
                  value={widgetConfig.cta_get_quote_url || ''}
                  onChange={(e) =>
                    setWidgetConfig((prev) => ({
                      ...prev,
                      cta_get_quote_url: e.target.value || undefined,
                    }))
                  }
                  placeholder="https://yoursite.com/quote-request"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Link to your quote request form or contact page
              </p>
            </div>
          </div>

          <Separator />

          {/* Contact Info */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-medium">Contact Information</Label>
              <p className="text-sm text-muted-foreground">Shown to customers on the results page</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="business-phone">Business Phone</Label>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <Input
                  id="business-phone"
                  type="tel"
                  value={widgetConfig.business_phone || ''}
                  onChange={(e) =>
                    setWidgetConfig((prev) => ({
                      ...prev,
                      business_phone: e.target.value || undefined,
                    }))
                  }
                  placeholder="(555) 123-4567"
                />
              </div>
              <p className="text-xs text-muted-foreground">10-digit US phone number</p>
            </div>
          </div>

          <Separator />

          {/* Watermark */}
          <div className="space-y-2">
            <Label htmlFor="watermark-text">Watermark Text</Label>
            <Input
              id="watermark-text"
              value={widgetConfig.watermark_text || ''}
              onChange={(e) =>
                setWidgetConfig((prev) => ({
                  ...prev,
                  watermark_text: e.target.value || undefined,
                }))
              }
              placeholder={tenant?.company_name || 'Your Company Name'}
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">
              Text shown on generated images (max 50 characters). Leave blank to use company name.
            </p>
          </div>

          <Button onClick={handleSaveWidget} disabled={updateWidget.isPending}>
            {updateWidget.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Widget Settings
          </Button>
        </CardContent>
      </Card>

      {/* Custom Color Dialogs */}
      <AddCustomColorDialog
        open={addColorDialogOpen}
        onOpenChange={setAddColorDialogOpen}
        onSubmit={handleAddCustomColor}
        isSubmitting={addCustomColor.isPending}
        error={customColorError}
      />

      <EditCustomColorDialog
        open={editColorDialogOpen}
        onOpenChange={setEditColorDialogOpen}
        color={selectedCustomColor}
        onSubmit={handleEditCustomColor}
        onDelete={() => {
          setEditColorDialogOpen(false);
          openDeleteDialog(selectedCustomColor!);
        }}
        isSubmitting={updateCustomColor.isPending}
        error={customColorError}
      />

      <DeleteColorConfirmDialog
        open={deleteColorDialogOpen}
        onOpenChange={setDeleteColorDialogOpen}
        color={selectedCustomColor}
        onConfirm={handleDeleteCustomColor}
        isDeleting={deleteCustomColor.isPending}
        isFeatured={
          selectedCustomColor
            ? (widgetConfig.featured_paint_colors || DEFAULT_FEATURED_COLORS).includes(
                selectedCustomColor.code
              )
            : false
        }
      />
    </div>
  );
}
